import React, { useEffect } from "react";
import i18n from "@microdoing/shared/i18n";
import {
  changeActionState,
  changeAnalysisType,
  changeComment,
  changeDonePercentage,
  changeRating,
  resetActionFeedbackSharedState,
} from "@microdoing/shared/services/action/actions";
import {
  getDebriefText,
  getStepSelected,
  getTeam,
  getUserCampaignRank,
  getUserCampaignTeamRank,
} from "@microdoing/shared/services/userCampaign/selectors";
import { Box, ExitButton, MainText, MainTitle } from "app/components/common";
import SocialSheet from "app/scenes/CurrentTrackScreen/DailyFocusScreen/SelectAction/SocialSheet";
import theme from "app/Theme/Theme";
import { useDispatch, useSelector } from "react-redux";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { resetUpload } from "@microdoing/shared/services/upload/actions";
import { getUserExperience } from "@microdoing/shared/services/user/selectors";
import normalize from "app/utils/normalize";
import SelectAnalysisTypeCard from "./SelectAnalysisTypeCard";
import { AnalysisType } from "@microdoing/shared/services/action/types";
import { useLaunchCamera } from "../useLaunchCamera";
import FastImage from "react-native-fast-image";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { selectStep } from "@microdoing/shared/services/userCampaign/actions";
import { isWeb, isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { useBackHandler } from "app/hooks/useBackHandler";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import WebAnalysisBoxing from "app/components/common/Web/WebAnalysisBoxing";
import Action from "./Action/Action";
import { breadCrumbsReflexiveAnalysisTypeScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import LinearGradient from "react-native-linear-gradient";
import Theme from "app/constants/Theme";
import { Dimensions, StatusBar } from "react-native";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Gesture, GestureDetector } from "react-native-gesture-handler";

const { width, height } = Dimensions.get("screen");

const PenImage = require("../../../../assets/images/Pencil.png");
const CameraImage = require("../../../../assets/images/Camera.png");
const MicroImage = require("../../../../assets/images/Micro.png");

const Jouvie3D = require("../../../../assets/images/Jouvie3D.png");

const ICON_WIDTH = normalize(30);

const JOUVIE_SIZE = normalize(140);

const AnimatedFastImage = isWeb
  ? FastImage
  : Animated.createAnimatedComponent(FastImage);

const AnimatedBox = isWeb ? Box : Animated.createAnimatedComponent(Box);

const AnimatedLinearGradient = !isWeb
  ? Animated.createAnimatedComponent(LinearGradient)
  : Box;

export interface IReflexiveAnalysisTypeScreenProps {}

const ReflexiveAnalysisTypeScreen = ({}: IReflexiveAnalysisTypeScreenProps) => {
  const stepSelected = useSelector(getStepSelected);
  const language = useSelector(getLanguage);
  const team = useSelector(getTeam);
  const userPoints = useSelector(getUserExperience);
  const teamRank = useSelector(getUserCampaignTeamRank);
  const userRank = useSelector(getUserCampaignRank);
  const debriefText = useSelector(getDebriefText);

  const gradientScale = useSharedValue(0.5);
  const buttonTranslateY = useSharedValue(25);
  const button2TranslateY = useSharedValue(25);
  const button3TranslateY = useSharedValue(25);
  const jouviePositionY = useSharedValue(0);

  const insets = useSafeAreaInsets();

  const gradientAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: gradientScale.value }],
    };
  });

  const jouvieAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: withSpring(
            interpolate(gradientScale.value, [0.5, 4], [50, -130]) +
              jouviePositionY.value,
          ),
        },
        {
          rotate: `${interpolate(jouviePositionY.value, [0, -45], [0, 25])}deg`,
        },
      ],
      opacity: interpolate(gradientScale.value, [3, 4], [0, 1]),
    };
  });

  const animatedButtonStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: buttonTranslateY.value,
        },
      ],
      opacity: interpolate(buttonTranslateY.value, [25, 10], [0, 1]),
    };
  });

  const animatedButtonStyle2 = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: button2TranslateY.value,
        },
      ],
      opacity: interpolate(button2TranslateY.value, [25, 10], [0, 1]),
    };
  });

  const animatedButtonStyle3 = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: button3TranslateY.value,
        },
      ],
      opacity: interpolate(button2TranslateY.value, [25, 10], [0, 1]),
    };
  });

  const isFocused = useIsFocused();

  const navigation = useNavigation();

  const [error, setError, openNativeModule] = useLaunchCamera({
    hasPhotoOption: true,
    hasVideoOption: true,
  });

  const {
    rating,
    totalDone,
    settings = {
      uploadInstruction: {},
      uploadOptions: ["video", "photo", "document"],
      isUploadRequired: false,
    },
  } = stepSelected;

  const teamPoints = team ? team.points : 0;
  const contentTitle = selectLanguage({
    text: stepSelected?.content?.title,
    language,
  });

  const {
    uploadOptions = ["video", "photo", "document"],
    isUploadRequired = false,
  } = settings;

  const hasAllOptions = uploadOptions.length === 0 && isUploadRequired;

  const hasVideoOption =
    !!uploadOptions?.find(el => el === "video") || hasAllOptions;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isFocused) {
      gradientScale.value = withDelay(100, withTiming(4, { duration: 550 }));
      buttonTranslateY.value = withDelay(isWebDesktop ? 0 : 200, withTiming(0));
      button2TranslateY.value = withDelay(
        isWebDesktop ? 0 : 275,
        withTiming(0),
      );
      button3TranslateY.value = withDelay(
        isWebDesktop ? 0 : 325,
        withTiming(0),
      );
    } else {
      gradientScale.value = withDelay(300, withTiming(0.5));
    }
  }, [isFocused]);

  useEffect(() => {
    dispatch(
      changeActionState({
        oldTeamPoints: teamPoints,
        oldUserPoints: userPoints,
        oldRank: userRank,
        oldTeamRank: teamRank,
      }),
    );
  }, []);

  const cancelValidationFeedback = () => {
    dispatch(resetUpload());
    dispatch(resetActionFeedbackSharedState());
    dispatch(changeRating(0));
    dispatch(changeDonePercentage(0));
    dispatch(changeComment(""));
    navigation.goBack();
  };

  useBackHandler(() => {
    if (isFocused) {
      dispatch(changeNavField({ isTopSafeAreaHidden: false }));
      cancelValidationFeedback();
      return true;
    }
    return false;
  });

  const selectAnalysisType = (optionSelected: AnalysisType) => () => {
    dispatch(resetUpload());

    dispatch(changeComment(""));
    if (!isWeb) {
      if (optionSelected === "video") {
        openNativeModule("video")();
      }
    }

    dispatch(changeAnalysisType(optionSelected));
    navigation.navigate("ReflexiveAnalysisScreen");
  };

  const handlePressActionTitle = () => {
    dispatch(changeActionState({ seeActionAgain: true }));
    dispatch(
      selectStep({
        stepSelected,
      }),
    );
    navigation.navigate("Action", {
      isComingFromAnalysis: true,
    });
  };

  const handlePressExit = () => {
    navigation.goBack();
    gradientScale.value = withTiming(0, { duration: 25 });
  };

  const question =
    selectLanguage({
      text: settings?.uploadInstruction,
      language,
    }) ||
    selectLanguage({ text: debriefText, language }) ||
    i18n.t("feeling-commentary-desc");

  const tapGesture = Gesture.Tap()
    .maxDuration(200)
    .onStart(() => {
      jouviePositionY.value = -45;
      jouviePositionY.value = withDelay(
        350,
        withSpring(0, {
          stiffness: 300,
          overshootClamping: true,
        }),
      );
    });

  return (
    <>
      <WebDesktopView breadcrumbsPath={breadCrumbsReflexiveAnalysisTypeScreen}>
        <WebAnalysisBoxing
          leftComponent={
            <Box
              flex={1}
              borderRadius={isWeb ? "inherit" : 0}
              padding={{ smallWeb: "l", web: "xl" }}
              paddingHorizontal={isWebDesktop ? "webM" : "sl"}
              style={{ paddingTop: insets.top ? insets.top : theme.spacing.s }}
              backgroundColor="neutral.white"
              zIndex={0}
              overflow="visible"
            >
              <AnimatedLinearGradient
                style={[
                  {
                    position: "absolute",
                    height: width,
                    width,
                    bottom: -width / 4,
                    borderRadius: width / 2,
                    zIndex: -1,
                  },
                  gradientAnimatedStyle,
                ]}
                colors={[
                  theme.colors["gradient.blueGradient.start"],
                  theme.colors["gradient.blueGradient.end"],
                ]}
              />
              <ExitButton
                onPress={handlePressExit}
                style={{
                  right: theme.spacing.m,
                  top: theme.spacing.sm + insets.top / 2,
                }}
                color="white"
              />
              <StatusBar hidden />
              {isWebDesktop && (
                <MainTitle
                  style={{
                    marginTop: theme.spacing.s,
                  }}
                  variant="l6-bold"
                  marginBottom="m"
                >
                  {i18n.t("reflexive-analysis")}
                </MainTitle>
              )}

              <MainTitle
                marginRight="m"
                variant="l8-bold"
                color={isWebDesktop ? "neutral.900" : "neutral.white"}
                style={{
                  marginTop: insets.top ? insets.top / 2 : theme.spacing.s,
                }}
              >
                {question}
              </MainTitle>
              <Box
                flex={1}
                justifyContent={isWebDesktop ? "flex-end" : "flex-end"}
                style={{ paddingBottom: isWebDesktop ? 0 : normalize(100) }}
              >
                <GestureDetector gesture={Gesture.Exclusive(tapGesture)}>
                  <AnimatedBox
                    position={"absolute"}
                    height={JOUVIE_SIZE}
                    width={JOUVIE_SIZE}
                    bottom={height / 4}
                    right={0}
                    zIndex={0}
                    style={jouvieAnimatedStyle}
                  >
                    <AnimatedFastImage
                      source={Jouvie3D}
                      style={[
                        {
                          zIndex: -1,
                          height: JOUVIE_SIZE,
                          width: JOUVIE_SIZE,
                        },
                      ]}
                    />
                  </AnimatedBox>
                </GestureDetector>
                <Animated.View style={animatedButtonStyle3}>
                  <SelectAnalysisTypeCard
                    onPressCard={selectAnalysisType("video")}
                    title={i18n.t("select-analysis-type-video-title")}
                    explanation={i18n.t(
                      "select-analysis-type-video-explanation",
                    )}
                    icon={
                      <FastImage
                        style={{
                          height: ICON_WIDTH,
                          width: ICON_WIDTH,
                        }}
                        source={CameraImage}
                      />
                    }
                  />
                </Animated.View>
                <Animated.View style={animatedButtonStyle2}>
                  <SelectAnalysisTypeCard
                    onPressCard={selectAnalysisType("text")}
                    title={i18n.t("select-analysis-type-text-title")}
                    explanation={i18n.t(
                      "select-analysis-type-text-explanation",
                    )}
                    icon={
                      <FastImage
                        style={{ height: ICON_WIDTH, width: ICON_WIDTH }}
                        source={PenImage}
                      />
                    }
                  />
                </Animated.View>
                <Animated.View style={animatedButtonStyle}>
                  <SelectAnalysisTypeCard
                    onPressCard={selectAnalysisType("audio")}
                    title={i18n.t("select-analysis-type-audio-title")}
                    explanation={i18n.t(
                      "select-analysis-type-audio-explanation",
                    )}
                    icon={
                      <FastImage
                        style={{ height: ICON_WIDTH, width: ICON_WIDTH }}
                        source={MicroImage}
                      />
                    }
                  />
                </Animated.View>
              </Box>
              {!isWebDesktop && (
                <SocialSheet
                  contentTitle={contentTitle}
                  rating={rating}
                  totalDone={totalDone}
                  numberOfComments={stepSelected?.numberOfComments || 0}
                  users={stepSelected?.users || []}
                  isSmall
                  hasShadow={false}
                />
              )}
            </Box>
          }
          rightComponent={<Action />}
        />
      </WebDesktopView>
    </>
  );
};

export default ReflexiveAnalysisTypeScreen;
